import { useEffect, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import type { WizardStepContext } from "@/base";
import { messageFor } from "@/lib/errors";
import { getSubmissionLocations } from "@/lib/submissions/brokerSubmissions";
import { withLocations, type WcDraft } from "@/lib/submissions/wc/draft";

/**
 * Reads the submission's locations (PMS-S702) into the draft when the
 * Locations step opens (BRK-S302): only it needs them, so `load` doesn't fetch
 * them. The read goes through `setLoaded`, so it is never an unsaved change
 * (BRK-S307). A failed read is a problem the step shows, with Retry.
 */
export function useSubmissionLocations(
  draft: WcDraft,
  setLoaded: WizardStepContext<WcDraft>["setLoaded"],
): { problem: string | null; retry: () => void } {
  const { authedRequest } = useAuth();
  const [problem, setProblem] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const submissionId = draft.submission?.id;
  const needed = draft.savedLocations === null;

  useEffect(() => {
    if (!needed || !submissionId) return;
    let cancelled = false;
    setProblem(null);
    getSubmissionLocations(authedRequest, submissionId).then(
      (saved) => {
        if (!cancelled) setLoaded((d) => withLocations(d, saved));
      },
      (err: unknown) => {
        if (!cancelled) setProblem(messageFor(err));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [needed, submissionId, attempt, authedRequest, setLoaded]);

  return { problem, retry: () => setAttempt((n) => n + 1) };
}
